// libs
import classNames from 'classnames'
import * as React from 'react'

// interfaces / constants
import { CLASS_PREFIX } from 'src/constants/'

// classes / components
import InputField from 'src/components/forms/input_field/input_field'
import SelectField from 'src/components/forms/select-field/select-field'

// helpers
import { doesBrowserSupportNativeTimeInput } from 'src/utils/browserDetection'

import './time_picker.scss'

interface IProps {
  className?: string;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  max?: string;
  min?: string;
  onChange?: (time: string) => void;
  value?: string;
  errorText?: string;
  valid?: boolean;
  useNative?: boolean;
}

interface IState {
  supportNativeTimePicker: boolean;
  hour: string;
  minute: string;
}

const clsBlock: string = CLASS_PREFIX + 'time-picker'
export default class TimePicker extends React.PureComponent<IProps, IState> {
  constructor (props: IProps) {
    super(props)
    const supportNativeTimePicker = doesBrowserSupportNativeTimeInput()

    const { value, useNative } = this.props
    const willUseNative = !!useNative && supportNativeTimePicker

    let hour = ''
    let minute = ''
    if (!willUseNative && value) {
      const date = value.split(':')
      hour = date[0]
      minute = date[1]
    }

    this.state = {
      hour,
      minute,
      supportNativeTimePicker
    }
    this.handleOnChangeHour = this.handleOnChangeHour.bind(this)
    this.handleOnChangeMinute = this.handleOnChangeMinute.bind(this)
    this.handleOnChangeTimePicker = this.handleOnChangeTimePicker.bind(this)
    this.triggerOnChange = this.triggerOnChange.bind(this)
  }

  public static defaultProps = { useNative: false }

  public componentDidUpdate (prevProps: IProps, prevState: IState) {
    const { useNative, value } = this.props
    const { supportNativeTimePicker } = this.state
    const willUseNative = !!useNative && supportNativeTimePicker

    if (
      !willUseNative &&
      (prevState.hour !== this.state.hour ||
        prevState.minute !== this.state.minute)
    ) {
      this.triggerOnChange()
    }

    if (!willUseNative && value && value !== prevProps.value) {
      const [h = '', m = ''] = value.split(':')
      const hh = this.padHourOrMinute(h)
      const mm = this.padHourOrMinute(m)
      if (hh !== this.state.hour || mm !== this.state.minute) {
        this.setState({ hour: hh, minute: mm })
      }
    }
  }

  public render () {
    const {
      className,
      disabled,
      errorText,
      max,
      min,
      value,
      label,
      required,
      valid,
      useNative
    } = this.props
    const { hour, minute, supportNativeTimePicker } = this.state
    const willUseNative = !!useNative && supportNativeTimePicker

    const hourOptions = Array.from({ length: 24 }, (_, i) => {
      const s = String(i).padStart(2, '0')
      return { value: s, text: s }
    })
    const minuteOptions = Array.from({ length: 60 }, (_, i) => {
      const s = String(i).padStart(2, '0')
      return { value: s, text: s }
    })

    const renderCustomTimePicker: JSX.Element = (<div>
      {!!label && (
        <div className={clsBlock + '__label'}>
          {label}
          {required ? ' *' : ''}
        </div>
      )}
      <div className={classNames(clsBlock + '__custom-picker', className)}>

        <SelectField
          label={'Uhr'}
          placeholder={'hh'}
          value={hour}
          onChange={this.handleOnChangeHour}
          disabled={disabled}
          required={required}
          options={hourOptions}
          errorText={errorText}
        />
        <SelectField
          label={'Minute'}
          placeholder={'mm'}
          value={minute}
          onChange={this.handleOnChangeMinute}
          disabled={disabled}
          required={required}
          options={minuteOptions}
        />
      </div>
    </div>
    )
    const renderNativeTimePicker: JSX.Element = (
      <InputField
        value={value}
        disabled={disabled}
        iconLeft={'clock'}
        label={label}
        className={classNames([clsBlock, className])}
        max={max}
        min={min}
        required={required}
        errorText={errorText}
        type={'time'}
        valid={valid}
        onChange={this.handleOnChangeTimePicker}
        floatLabel={false}
        name="time"
      />
    )

    return willUseNative ? renderNativeTimePicker : renderCustomTimePicker
  }

  private handleOnChangeTimePicker (value: string) {
    const { onChange } = this.props
    if (onChange) {
      onChange(value)
    }
  }

  private triggerOnChange () {
    const { onChange } = this.props
    const { hour, minute } = this.state

    if (hour === '') {
      return
    }
    if (minute === '') {
      return
    }

    if (onChange) {
      onChange(`${this.padHourOrMinute(hour)}:${this.padHourOrMinute(minute)}`)
    }
  }

  private handleOnChangeHour (value: string) {
    this.setState({ hour: value })
  }

  private handleOnChangeMinute (value: string) {
    this.setState({ minute: value })
  }

  private padHourOrMinute (input: string) {
    return input.length === 1 ? `0${input}` : input
  }
}
